import { PlusIcon } from 'lucide-react';

import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import {
  getDefinitionForQueryFilter,
  getDefinitionForSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/utils/simpleFiltersUtils';
import type { FlatGraphNode } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { Operator, QueryFilter } from '@/types';

import { NodeFiltersActions } from './NodeFiltersActions';
import { NodeFiltersHeader } from './NodeFiltersHeader';
import { NodeFiltersList } from './NodeFiltersList';

interface NodeFilterGroupProps {
  node: FlatGraphNode;
  filters: QueryFilter[]; // Normalized filters array (logical groups)
  editable: boolean;
  isLoadingFields: boolean;
  filterDefinitions: SimpleFilterDefinition[] | null;
  firstLabel: string;
  onUpdateFilters?: (nodeId: string, filters: QueryFilter[]) => void;
}

/**
 * NodeFilterGroup - Renders multiple filter groups for normalized queries
 *
 * Takes normalized filters (array of logical groups) and renders separate
 * Header/List/Actions sections for each group with visual separation.
 */
export const NodeFilterGroup = ({
  node,
  filters,
  editable,
  isLoadingFields,
  filterDefinitions,
  firstLabel,
  onUpdateFilters,
}: NodeFilterGroupProps) => {
  /**
   * Extract displayable filters and operator from a single logical group
   * Adapted from NodeCard's extractDisplayFilters for per-group use
   */
  const extractDisplayFilters = (
    logicalGroupFilter: QueryFilter
  ): {
    displayFilters: QueryFilter[];
    currentOperator: 'AND' | 'OR';
  } => {
    return {
      displayFilters: logicalGroupFilter.value as QueryFilter[],
      currentOperator: logicalGroupFilter.operator as 'AND' | 'OR',
    };
  };

  /**
   * Toggle AND/OR operator for a specific group
   * Rebuilds entire normalized filters array with updated group
   */
  const handleToggleOperator = (
    groupIndex: number,
    currentOperator: 'AND' | 'OR'
  ): QueryFilter[] => {
    const newOperator: 'AND' | 'OR' = currentOperator === 'AND' ? 'OR' : 'AND';

    // Create new filters array with updated group at groupIndex
    return filters.map((filter, idx) =>
      idx === groupIndex
        ? {
            ...filter,
            operator: newOperator,
          }
        : filter
    );
  };

  /**
   * Add new filter to a specific group
   * Uses first field definition to create default filter
   */
  const handleAddFilter = (groupIndex: number): QueryFilter[] => {
    // Get first available field definition for defaults
    const firstFieldDef = filterDefinitions ? filterDefinitions[0] : null;

    // Create new filter with proper defaults
    const newFilter: QueryFilter = firstFieldDef
      ? firstFieldDef.translateForAPI({
          field: firstFieldDef.uiField,
          operator: firstFieldDef.operators[0],
          value: '',
        })
      : {
          field: '',
          operator: '=' as Operator,
          value: '',
        };

    // Create new filters array with new filter added to group at groupIndex
    return filters.map((filter, idx) =>
      idx === groupIndex
        ? {
            ...filter,
            value: [...(filter.value as QueryFilter[]), newFilter],
          }
        : filter
    );
  };

  /**
   * Handle filter change within a specific group
   * Translates SimpleFilter back to QueryFilter and rebuilds normalized structure
   */
  const handleSimpleFilterChange = (
    groupIndex: number,
    leafIndex: number,
    updatedSimpleFilter: Partial<SimpleFilter>
  ) => {
    if (!filterDefinitions || !onUpdateFilters) return;

    const logicalGroup = filters[groupIndex];
    const { displayFilters } = extractDisplayFilters(logicalGroup);

    // Get current simple filters for this group
    const simpleFiltersForGroup = displayFilters.map((filter, index) => {
      const definition = filter.field
        ? getDefinitionForQueryFilter(filterDefinitions, filter)
        : undefined;

      if (definition) {
        return {
          ...definition.translateForUI(filter),
          index,
        };
      }

      return {
        field: filter.field,
        operator: String(filter.operator),
        value: Array.isArray(filter.value)
          ? filter.value.map(String)
          : String(filter.value),
        index,
      };
    });

    // Merge changes into current simple filter
    const newSimpleFilter: SimpleFilter = {
      ...simpleFiltersForGroup[leafIndex],
      ...updatedSimpleFilter,
    };

    // Get field definition for translation
    const definition = getDefinitionForSimpleFilter(
      filterDefinitions,
      newSimpleFilter
    );

    // Translate back to QueryFilter
    const updatedQueryFilter: QueryFilter = definition
      ? definition.translateForAPI(newSimpleFilter)
      : {
          field: newSimpleFilter.field,
          operator: newSimpleFilter.operator as Operator,
          value: newSimpleFilter.value,
        };

    // Rebuild display filters with updated filter
    const newDisplayFilters = displayFilters.map((f, i) =>
      i === leafIndex ? updatedQueryFilter : f
    );

    // Reconstruct entire normalized filters array with updated group
    const newFilters = filters.map((filter, idx) =>
      idx === groupIndex
        ? {
            ...filter,
            value: newDisplayFilters,
          }
        : filter
    );

    onUpdateFilters(node.id, newFilters);
  };

  /**
   * Delete filter from a specific group
   * Removes leaf filter at index and rebuilds normalized structure
   */
  const handleSimpleFilterDelete = (groupIndex: number, leafIndex: number) => {
    if (!filterDefinitions || !onUpdateFilters) return;

    const logicalGroup = filters[groupIndex];
    const { displayFilters } = extractDisplayFilters(logicalGroup);

    // Remove filter at specified index
    const newDisplayFilters = displayFilters.filter((_, i) => i !== leafIndex);

    if (newDisplayFilters.length === 0) {
      const newFilters = filters.filter((_, idx) => idx !== groupIndex);
      onUpdateFilters(node.id, newFilters);
      return;
    }

    // Reconstruct entire normalized filters array with updated group
    const newFilters = filters.map((filter, idx) =>
      idx === groupIndex
        ? {
            ...filter,
            value: newDisplayFilters,
          }
        : filter
    );

    onUpdateFilters(node.id, newFilters);
  };

  /**
   * Add new empty filter group to the normalized filters array
   * Creates a new logical group with OR operator and empty value array
   */
  const handleAddFilterGroup = async () => {
    if (!filterDefinitions || !onUpdateFilters) return;

    const defaultFilterDefinition = filterDefinitions[0];

    const defaultSimpleFilter = {} as SimpleFilter;

    defaultSimpleFilter.field = defaultFilterDefinition.uiField;
    defaultSimpleFilter.operator = defaultFilterDefinition.operators[0];
    defaultSimpleFilter.value = '';

    const defaultFilter =
      defaultFilterDefinition.translateForAPI(defaultSimpleFilter);

    // Create new empty logical group
    const newGroup: QueryFilter = {
      field: '',
      operator: 'OR' as Operator,
      value: [defaultFilter],
    };

    // Append to existing filters array
    const newFilters = [...filters, newGroup];

    onUpdateFilters(node.id, newFilters);
  };

  return (
    <>
      {filters.map((logicalGroupFilter, groupIndex) => {
        const { displayFilters, currentOperator } =
          extractDisplayFilters(logicalGroupFilter);

        // Convert displayFilters to SimpleFilters for this group
        const simpleFilters: SimpleFilter[] = displayFilters.map(
          (filter, index) => {
            const definition = filter.field
              ? getDefinitionForQueryFilter(filterDefinitions, filter)
              : undefined;

            if (definition) {
              return {
                ...definition.translateForUI(filter),
                index,
              };
            }

            return {
              field: filter.field,
              operator: String(filter.operator),
              value: Array.isArray(filter.value)
                ? filter.value.map(String)
                : String(filter.value),
              index,
            };
          }
        );

        return (
          <div key={groupIndex} className="border-chariot-stroke border-t p-4">
            <NodeFiltersHeader
              node={node}
              filters={[logicalGroupFilter]}
              editable={editable}
              onUpdateFilters={onUpdateFilters}
              extractDisplayFilters={() => ({
                displayFilters,
                currentOperator,
              })}
              handleToggleOperator={() =>
                handleToggleOperator(groupIndex, currentOperator)
              }
            />

            <NodeFiltersList
              simpleFilters={simpleFilters}
              editable={editable}
              isLoadingFields={isLoadingFields}
              filterDefinitions={filterDefinitions}
              onFilterChange={(leafIndex, updatedFilter) =>
                handleSimpleFilterChange(groupIndex, leafIndex, updatedFilter)
              }
              onFilterDelete={leafIndex =>
                handleSimpleFilterDelete(groupIndex, leafIndex)
              }
            />

            <NodeFiltersActions
              node={node}
              filters={[logicalGroupFilter]}
              editable={editable}
              firstLabel={firstLabel}
              onUpdateFilters={onUpdateFilters}
              handleAddFilter={() => handleAddFilter(groupIndex)}
            />
          </div>
        );
      })}

      {/* Add Filter Group button - only show when editable */}
      {editable && onUpdateFilters && (
        <div className="border-chariot-stroke border-t p-4">
          <button
            onClick={handleAddFilterGroup}
            className="border-chariot-stroke text-chariot-brand-primary hover:bg-chariot-background-secondary flex w-full items-center justify-center gap-2 rounded border py-1.5 text-xs font-medium transition-colors"
            data-testid={`add-filter-group-${node.id}`}
            aria-label={`Add filter group to ${firstLabel} node`}
          >
            <PlusIcon className="h-3 w-3" />
            <span>Add Filter Group</span>
          </button>
        </div>
      )}
    </>
  );
};
